// 聊天顶部标题栏：左返回+未读徽章、中间标题胶囊（headerRight 仍在 Stack.Screen 中配置）
import { observer } from "mobx-react-lite"
import React, { useRef } from "react"
import { ActivityIndicator, Pressable } from "~/ui/primitives"
import { useStores } from "../../models"
import { useActiveChat } from "~hooks/useActiveChat"
import styled, { useTheme } from "~/ui/styled"
import { CenterFlex, Flex, IconFont } from "~components/El"
import useNavigation from "~hooks/useNavigation"
import { formatOnlineDate, decorateOnlinePresenceLine } from "~utils/date"

const pillBg = "rgba(255, 255, 255, 0.62)"

const LeftPill = styled(CenterFlex)`
  flex-direction: row;
  align-items: center;
  width: auto;
  padding: 6px 10px 6px 8px;
  border-radius: ${({ theme }) => theme.radii.full};
`

const UnreadBadge = styled(CenterFlex)`
  margin-left: 6px;
  background-color: ${({ theme }) => theme.colors.text};
  border-radius: ${({ theme }) => theme.radii.full};
  padding: 2px 7px;
  min-width: 22px;
  align-items: center;
  justify-content: center;
`

const UnreadText = styled.Text`
  color: ${({ theme }) => theme.colors.blockBackground};
  font-size: 11px;
  font-weight: 600;
`

const CenterPill = styled.Pressable`
  max-width: 240px;
  padding: 8px 16px;
  align-items: center;
  justify-content: center;
`

const TitleRow = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const TitleText = styled.Text`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
`

const SubtitleText = styled.Text`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondary};
  margin-top: 2px;
  text-align: center;
`

/** 左侧：返回 + 全局未读角标（与聊天列表徽章逻辑一致） */
export const ChatHeaderLeft = observer(() => {
  const theme = useTheme()
  const navigation = useNavigation()
  const { messageStore } = useStores()
  const backGuardRef = useRef(false)
  const totalUnread = messageStore.unread
  const badge =
    totalUnread > 0 ? (totalUnread > 99 ? "99+" : String(totalUnread)) : null

  return (
    <LeftPill>
      <Pressable
        onClick={() => {
          if (backGuardRef.current) return
          backGuardRef.current = true
          window.setTimeout(() => {
            backGuardRef.current = false
          }, 400)

          if (navigation.canGoBack()) {
            navigation.goBack()
            return
          }
          navigation.navigate("ChatList")
        }}
      >
        <IconFont name={"left"} color={theme.colors.text} size={18} />
      </Pressable>
      {badge ? (
        <UnreadBadge>
          <UnreadText>{badge}</UnreadText>
        </UnreadBadge>
      ) : null}
    </LeftPill>
  )
})

interface CenterProps {
  onClick: () => void
}

/** 中间：标题 + 副标题，点击进入资料/群信息 */
const ChatHeaderCenterBase: React.FC<CenterProps> = ({ onClick }) => {
  const theme = useTheme()
  const { contactStore, websocketStore, walletStore } = useStores()
  const { chat: current } = useActiveChat()

  if (!current) {
    return null
  }

  let title = current.displayTitle
  if (current.typing) {
    const name = contactStore.getDisplayName(current.typing)
    title = current.chatTypeEnum === "GROUP" ? `${name} 输入中...` : "正在输入中..."
  }

  let subtitle: string | null = null
  if (websocketStore.loading) {
    subtitle = websocketStore.stateText + (websocketStore.loading ? "..." : "")
  } else if (["GROUP", "CHANNEL"].includes(current.chatTypeEnum)) {
    const n = current.members.length
    // const online = current.members.filter((m) => m.user?.online).length
    const unit = current.chatTypeEnum === "GROUP" ? "位成员" : "位订阅者"
    subtitle = `${n}${unit}`
  } else if (current.receiver) {
    const { last_seen, online } = current.receiver
    /** 与接口一致：1=实时在线；缺省或非 2 视作 1；2=仅最近活跃 */
    const recentOnlyRule = walletStore.online_display_rule === 2
    if (recentOnlyRule) {
      subtitle = last_seen ? formatOnlineDate(last_seen) : null
    } else if (online) {
      subtitle = "在线"
    } else if (last_seen) {
      subtitle = decorateOnlinePresenceLine(formatOnlineDate(last_seen))
    }
  }

  return (
    <CenterPill onClick={onClick}>
      <TitleRow>
        <TitleText numberOfLines={1}>{title}</TitleText>
        {websocketStore.loading ? (
          <ActivityIndicator style={{ marginLeft: 6 }} size="small" color={theme.colors.secondaryText} />
        ) : null}
      </TitleRow>
      {subtitle ? <SubtitleText numberOfLines={1}>{subtitle}</SubtitleText> : null}
    </CenterPill>
  )
}

export const ChatHeaderCenter = observer(ChatHeaderCenterBase)
